<script setup lang="ts">
  import { Button, Typo, Tag, Modal, type TypoVariant } from '@travelata-libs/ui-kit';
  import { formatPhone } from '@travelata-libs/utils';
  import { ref, computed, nextTick } from 'vue';
  import type { LeadBannerPayload } from './LeadBanner.types';
  import LeadBannerAvatars from './LeadBannerAvatars.vue';
  import LeadBannerForm from './LeadBannerForm.vue';
  import { TAG_RESPONSE_TIME, TAG_EXPERIENCE } from './lib';

  interface LeadBannerProps {
    title: string;
    subtitle?: string;
    subtitleVariant?: `${TypoVariant}`;
    mobile?: boolean;
    modelValue: LeadBannerPayload;
    isSubmitted?: boolean;
    loading?: boolean;
    userAgreementUrl: string;
    privacyPolicyUrl: string;
    zIndex?: number;
    dataProcessingUrl: string;
    buttonText: string;
  }

  interface LeadBannerEmits {
    (e: 'submit', payload: LeadBannerPayload): void;
    (e: 'update:modelValue', value: LeadBannerPayload): void;
  }

  const emit = defineEmits<LeadBannerEmits>();

  const props = withDefaults(defineProps<LeadBannerProps>(), {
    subtitle: undefined,
    subtitleVariant: 'body-l-400',
    mobile: false,
    zIndex: 5,
    isSubmitted: false,
    loading: false,
  });

  const isModalOpen = ref(false);
  const formRef = ref<InstanceType<typeof LeadBannerForm> | null>(null);

  const maskedPhone = computed(() => formatPhone(props.modelValue.phone));

  const containerClasses = computed(() => ({
    'tl-lead-banner': true,
    'tl-lead-banner_mobile': props.mobile,
  }));

  const openModal = async () => {
    isModalOpen.value = true;
    await nextTick();
    formRef.value?.focusName();
  };

  const closeModal = () => {
    isModalOpen.value = false;
  };

  const handleSubmit = (payload: LeadBannerPayload) => {
    if (props.isSubmitted || props.loading) return;
    emit('submit', payload);
  };

  const formModel = computed({
    get: () => props.modelValue,
    set: (value) => {
      emit('update:modelValue', value);
    },
  });

  defineExpose({
    closeModal,
  });
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <div :class="containerClasses" data-test-id="lead-banner">
    <template v-if="!mobile">
      <div v-if="!isSubmitted" class="tl-lead-banner__content" data-test-id="lead-banner-content">
        <div class="tl-lead-banner__heading-wrapper">
          <div class="tl-lead-banner__heading">
            <Typo variant="h-3" class="tl-lead-banner__title">{{ title }}</Typo>
            <Typo v-if="subtitle" :variant="subtitleVariant" class="tl-lead-banner__subtitle">
              {{ subtitle }}
            </Typo>
          </div>

          <div class="tl-lead-banner__social">
            <LeadBannerAvatars :mobile="mobile" />

            <div class="tl-lead-banner__tags">
              <Tag color="green" appearance="primary">{{ TAG_RESPONSE_TIME }}</Tag>
              <Tag color="green" appearance="primary">{{ TAG_EXPERIENCE }}</Tag>
            </div>
          </div>
        </div>

        <LeadBannerForm
          v-model="formModel"
          class="tl-lead-banner__form"
          :loading="loading"
          :mobile="mobile"
          :user-agreement-url="userAgreementUrl"
          :privacy-policy-url="privacyPolicyUrl"
          :button-text="buttonText"
          :data-processing-url="dataProcessingUrl"
          @submit="handleSubmit"
        />
      </div>

      <div v-else class="tl-lead-banner__success" data-test-id="lead-banner-success">
        <Typo variant="h-3" class="tl-lead-banner__success-title">Заявка принята</Typo>
        <Typo variant="body-l-400" class="tl-lead-banner__success-subtitle">
          Вам перезвонит специалист на номер {{ maskedPhone }}
        </Typo>

        <LeadBannerAvatars centered :mobile="mobile" />
      </div>
    </template>

    <template v-else>
      <div v-if="!isSubmitted" class="tl-lead-banner__content-mobile" data-test-id="lead-banner-content-mobile">
        <Typo variant="body-l-500" class="tl-lead-banner__title">{{ title }}</Typo>

        <LeadBannerAvatars mobile />

        <Button data-test-id="lead-banner-mobile-btn" @click="openModal">{{ buttonText }}</Button>
        <Modal v-if="isModalOpen" :mobile="true" :z-index="zIndex" @close="isModalOpen = false">
          <Typo class="tl-lead-banner__modal-header" variant="h-5">Контакты для связи</Typo>
          <LeadBannerForm
            ref="formRef"
            v-model="formModel"
            class="tl-lead-banner__modal-form"
            data-test-id="lead-banner-modal-form"
            :mobile="mobile"
            button-text="Отправить заявку"
            :loading="loading"
            :user-agreement-url="userAgreementUrl"
            :privacy-policy-url="privacyPolicyUrl"
            :data-processing-url="dataProcessingUrl"
            @submit="handleSubmit"
          />
        </Modal>
      </div>

      <div v-else class="tl-lead-banner__success-mobile" data-test-id="lead-banner-success-mobile">
        <Typo variant="body-l-500" class="tl-lead-banner__success-title">
          Заявка принята. Вам перезвонит специалист на номер {{ maskedPhone }}
        </Typo>

        <LeadBannerAvatars mobile centered />
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
  .tl-lead-banner {
    $root: &;

    background-color: var(--color-bg-primary);
    border-radius: var(--rounding-s);
    box-shadow: var(--shadow-s);
    color: var(--color-text-primary);
    display: flex;
    justify-content: center;
    padding: var(--spacing-xxxl) var(--spacing-xxl);

    &__content {
      align-items: center;
      display: flex;
      gap: var(--spacing-xxxl);
      justify-content: space-between;
      max-width: 952px;
      width: 100%;
    }

    &__heading-wrapper {
      display: flex;
      flex: 1 1 420px;
      flex-direction: column;
      height: 100%;
      justify-content: space-between;
      max-width: 420px;
      min-width: 342px;
    }

    &__heading {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xs);
    }

    &__social {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-s);
    }

    &__tags {
      display: flex;
      gap: var(--spacing-xs);
    }

    &__form {
      display: flex;
      flex-direction: column;
      height: 100%;
      max-width: 420px;
      min-width: 282px;
      width: 100%;
    }

    &__success {
      align-items: center;
      display: flex;
      flex-direction: column;
      justify-content: center;
      max-width: 782px;
      text-align: center;
      width: 100%;
    }

    &__success-title {
      margin-bottom: var(--spacing-xs);
    }

    &__success-subtitle {
      margin-bottom: var(--spacing-xxl);
    }

    #{$root}__success-title {
      margin-bottom: var(--spacing-s);
    }

    &_mobile {
      padding: var(--spacing-l) var(--spacing-m);
    }

    &__modal-header {
      color: var(--color-text-primary);
      margin-bottom: calc(var(--spacing-l) + var(--spacing-m));
      text-align: center;
    }

    &__content-mobile {
      align-items: center;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-m);
      text-align: center;
    }

    &__modal-form {
      display: flex;
      flex-direction: column;
    }

    &__success-mobile {
      align-items: center;
      display: flex;
      flex-direction: column;
      text-align: center;
    }
  }
</style>
